import type { JSX } from 'react';

import { CtLogo } from '@/components/brand/index.js';
import { getRuntimeConfig } from '@/lib/runtime-config.js';
import { safeHttpUrl } from '@/lib/safe-url.js';

/**
 * A bare email address, the one shape worth turning into a mailto. Anything
 * looser would start guessing at free text the operator wrote for humans.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * A value the operator already wrote as a full mailto URI — the contact field is
 * free text, so this happens. It also matches EMAIL_PATTERN, so it has to be
 * recognised first: prefixing it again yields a dead `mailto:mailto:…` href.
 */
const MAILTO_PATTERN = /^mailto:\S/i;

/**
 * The href for a contact value, and whether it leaves the app. An http(s) URL
 * gets a new tab; a mailto must not, because the browser hands it to the mail
 * client and leaves an empty tab behind — inside a Teams tab, a dead panel.
 * A value that is neither returns an empty href and is printed as text.
 */
function contactLink(contact: string): { href: string; external: boolean } {
  const httpUrl = safeHttpUrl(contact);
  if (httpUrl !== '') return { href: httpUrl, external: true };
  if (MAILTO_PATTERN.test(contact)) return { href: contact, external: false };
  if (EMAIL_PATTERN.test(contact))
    return { href: `mailto:${contact}`, external: false };
  return { href: '', external: false };
}

/**
 * The one actionable line on this screen: who to ask for access. The value comes
 * from the runtime config (GET /v1/config → `accessRequestContact`), so an
 * operator repoints it without a new bundle, and an empty value — the default,
 * and what an older server sends — renders nothing at all.
 *
 * The value is free text: an address, a channel link, or words. An http(s) URL
 * and an email (bare or already written as a mailto URI) become links;
 * everything else is printed as typed. `safeHttpUrl` keeps a misconfigured
 * `javascript:`-style value out of the href — it matches none of the three
 * shapes, so no path can put it there.
 *
 * It still names no reason for the denial — that is the point of this screen.
 */
function AccessRequestLine(): JSX.Element | null {
  const contact = getRuntimeConfig().accessRequestContact.trim();
  if (contact === '') return null;

  const { href, external } = contactLink(contact);

  return (
    <p className="text-sm leading-relaxed text-muted-foreground">
      Request access from{' '}
      {href === '' ? (
        <span className="text-foreground">{contact}</span>
      ) : (
        <a
          href={href}
          target={external ? '_blank' : undefined}
          rel={external ? 'noopener noreferrer' : undefined}
          className="text-primary hover:underline"
        >
          {contact}
        </a>
      )}
    </p>
  );
}

/**
 * Full-page screen for a person the platform will not let in: the token
 * exchange answered 403, or an authorized call did.
 *
 * One text for every cause — not in the group, gate on with no claim,
 * membership unconfirmed — and it names none of them. Which check failed is
 * the platform's business; what the reader needs is who to ask.
 *
 * It renders markup and one cached config value, nothing else. This is the only
 * thing a denied person sees inside a Teams tab, where Entra cannot draw its own
 * error page in the embedded frame, so it must not depend on a token, a fetch,
 * or an SDK that is only there in one host — `getRuntimeConfig()` is a
 * synchronous read of what boot already loaded, and falls back to defaults when
 * it did not.
 *
 * No sidebar, no header — rendered at /access-denied (BARE_ROUTES) and by
 * AuthGate in place of the sign-in screen.
 */
export function AccessDeniedScreen(): JSX.Element {
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center overflow-hidden bg-background px-6">
      <div className="flex w-full max-w-md flex-col items-center gap-6 text-center">
        <div className="flex items-center gap-3.5">
          <CtLogo
            variant="mark"
            tone="mono"
            className="size-11 text-[#18399b] [[data-theme='dark']_&]:text-white"
            aria-label="Customertimes"
          />
          <h1 className="text-2xl font-bold leading-[1.1] tracking-[-0.01em] text-foreground">
            Agent
          </h1>
        </div>

        <h2 className="text-base font-semibold text-foreground">
          You don&rsquo;t have access
        </h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Access to this application is granted by the IT department. Ask them
          for it, then sign in again.
        </p>
        <AccessRequestLine />
      </div>
    </div>
  );
}
